import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Lock, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [mismatch, setMismatch] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setMismatch(true); return; }
    setMismatch(false);
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated. Sign in with your new password.");
      navigate("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex relative bg-primary text-primary-foreground p-12 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <Link to="/" className="relative flex items-center gap-2.5">
          <div className="grid h-10 w-10 place-items-center rounded-md bg-primary-foreground text-primary font-display font-bold">R</div>
          <div className="font-display text-lg font-bold tracking-tight">CA Ritika Singh</div>
        </Link>
        <div className="relative max-w-md">
          <ShieldCheck className="h-10 w-10 mb-6 text-accent" />
          <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight leading-tight">
            Choose a strong password to keep your tax data safe.
          </h2>
          <p className="mt-4 text-primary-foreground/75 leading-relaxed">
            Minimum 8 characters. This link expires in 1 hour — if it's expired, request a new one.
          </p>
        </div>
        <div className="relative text-xs text-primary-foreground/60">© {new Date().getFullYear()} CA Ritika Singh · Made in India</div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {!token ? (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-5 text-sm flex items-start gap-3">
              <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-semibold text-destructive">Invalid reset link</div>
                <div className="mt-1 text-muted-foreground">This link is missing the reset token. <Link to="/forgot-password" className="underline">Request a new one.</Link></div>
              </div>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl font-bold tracking-tight">Set new password</h1>
              <p className="mt-2 text-sm text-muted-foreground">Must be at least 8 characters.</p>

              <form onSubmit={onSubmit} className="mt-8 space-y-4">
                <div>
                  <Label htmlFor="password">New password</Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      required
                      minLength={8}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-11 pl-9"
                      placeholder="Minimum 8 characters"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirm">Confirm new password</Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirm"
                      type="password"
                      required
                      minLength={8}
                      value={confirm}
                      onChange={(e) => { setConfirm(e.target.value); setMismatch(false); }}
                      className={`h-11 pl-9 ${mismatch ? "border-destructive" : ""}`}
                      placeholder="Repeat password"
                    />
                  </div>
                  {mismatch && <p className="mt-1.5 text-xs text-destructive">Passwords don't match</p>}
                </div>
                <Button type="submit" disabled={loading} className="w-full h-11">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
                </Button>
              </form>

              <p className="mt-6 text-sm text-muted-foreground">
                Link expired? <Link to="/forgot-password" className="font-semibold text-foreground hover:underline">Request a new one</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
